import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { checkCode } from './billingAccess'
import { subIsTeams, userHasCardOnFile } from './'
import './GiftCodeForm.less'

const GiftCodeForm = forwardRef(({ user, onCodeValidated, initialCode = '', disabled = false }, ref) => {
  const [code, setCode] = useState(initialCode)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [teamsDowngradeError, setTeamsDowngradeError] = useState(false)
  const [cardRequiredError, setCardRequiredError] = useState(false)
  const hasCard = !!userHasCardOnFile(user)

  // Auto-validate when initialCode is provided
  useEffect(() => {
    if (initialCode && initialCode.trim() && !disabled) {
      handleSubmit()
    }
  }, [initialCode, disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    reset: () => {
      setCode('')
      setLoading(false)
      setError(false)
      setIsValid(false)
      setTeamsDowngradeError(false)
      setCardRequiredError(false)
    }
  }))

  const handleSubmit = async (evt) => {
    if (evt) {
      evt.preventDefault()
    }
    
    if (!code || isValid) return
    
    // Check if user has a card on file
    if (!hasCard) {
      setCardRequiredError(true)
      setError(false)
      setTeamsDowngradeError(false)
      return
    }
    
    setLoading(true)
    setError(false)
    setTeamsDowngradeError(false)
    setCardRequiredError(false)
    
    try {
      const res = await checkCode(code)
      const valid = res && res.valid === true
      
      if (valid) {
        // Check if user has Teams subscription and code is for regular AWN+
        const isTeamsUser = user && user.plus && subIsTeams(user.plus)
        const isRegularAwnPlusCode = res.productName && /^awn-plus-account-/.test(res.productName)
        
        if (isTeamsUser && isRegularAwnPlusCode) {
          setTeamsDowngradeError(true)
          setError(false)
          setIsValid(false)
          return
        }
        
        setIsValid(true)
        setError(false)
        setTeamsDowngradeError(false)
        
        // Pass validated code info back to parent
        if (onCodeValidated) {
          onCodeValidated({
            code,
            productName: res.productName,
            valid: true
          })
        }
      } else {
        setError(true)
        setIsValid(false)
        setTeamsDowngradeError(false)
      }
    } catch (err) {
      setError(true)
      setIsValid(false)
      setTeamsDowngradeError(false)
    } finally {
      setLoading(false)
    }
  }

  const getButtonText = () => {
    if (isValid) return 'Applied'
    if (error || teamsDowngradeError || cardRequiredError) return 'Try again'
    return 'Apply'
  }

  // Handle input change to clear card required error
  const handleInputChange = (evt) => {
    const newCode = evt.target.value.toUpperCase()
    setCode(newCode)
    
    // Show card required error if user types without a card
    if (!hasCard && newCode.length > 0) {
      setCardRequiredError(true)
      setError(false)
    } else {
      setCardRequiredError(false)
    }
  }

  return (
    <div className='payment-gift-code-form'>
      {!hasCard && (
        <div className='card-required-box'>
          <p><strong>To redeem gift code, please register your card first.</strong></p>
          <p>Your card is required to verify your account and ensure continued access to your premium features after your free year. You won't be charged today, and you can turn off auto-renewal at any time.</p>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className='input-group'>
          <input 
            type='text' 
            placeholder='Redeem gift code' 
            disabled={disabled || isValid || loading}
            className={(error || cardRequiredError) ? 'error' : ''}
            value={code} 
            onChange={handleInputChange} 
          />
          <button 
            type='submit' 
            disabled={disabled || isValid || loading || !code || teamsDowngradeError || !hasCard}
            className='btn btn-primary'
          >
            {getButtonText()}
          </button>
        </div>
        {error && <p className='error'>Invalid code</p>}
        {cardRequiredError && <p className='error'>Please, register your card first.</p>}
        {teamsDowngradeError && (
          <p className='error'>
            This gift code is for AWN+ which cannot be applied to your AWN+ Teams subscription. 
            Please wait until your Teams subscription expires before using this gift code.
          </p>
        )}
        <ul>
          <li>Gift Code activates 1 year of free subscription</li>
          <li>Subscription renews automatically unless canceled</li>
          <li>Standard annual fee applies at renewal</li>
        </ul>
      </form>
    </div>
  )
})

GiftCodeForm.propTypes = {
  user: PropTypes.object.isRequired,
  onCodeValidated: PropTypes.func.isRequired,
  initialCode: PropTypes.string,
  disabled: PropTypes.bool
}

GiftCodeForm.defaultProps = {
  initialCode: '',
  disabled: false
}

GiftCodeForm.displayName = 'GiftCodeForm'

export default GiftCodeForm